import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Inbox, RefreshCw, TriangleAlert } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import {
  getMyPmiDetectionHistory,
  getMyPmiDetectionHistoryDetail,
  MyPmiDetectionHistoryDetail,
  MyPmiDetectionHistoryElement,
  MyPmiDetectionHistoryItem,
  MyPmiDetectionResultFilter,
} from '../services/api';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError } from '../utils/pdaFeedback';
import { navigateTo } from '../utils/navigation';

type ResultFilter = 'all' | MyPmiDetectionResultFilter;

const PAGE_SIZE = 20;

function formatDetectedAt(value: string) {
  const matched = value.match(/^\d{4}-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (matched) {
    return `${matched[1]}-${matched[2]} ${matched[3]}:${matched[4]}`;
  }
  return value || '--';
}

function formatDetailDate(value: string | null | undefined) {
  if (!value) {
    return '--';
  }
  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (matched) {
    return `${matched[1]}-${matched[2]}-${matched[3]} ${matched[4]}:${matched[5]}${matched[6] ? `:${matched[6]}` : ''}`;
  }
  return value;
}

function formatElementValue(value: number | string | null) {
  if (value === null || value === '') {
    return '未检出';
  }
  const text = String(value);
  return text.endsWith('%') ? text : `${text}%`;
}

function formatElementRange(element: MyPmiDetectionHistoryElement) {
  if (!element.required && element.pmi_min === null && element.pmi_max === null) {
    return '非必检元素，不参与判定';
  }
  if (element.pmi_min !== null && element.pmi_max !== null) {
    return `要求 ${element.pmi_min} - ${element.pmi_max}%`;
  }
  if (element.pmi_min !== null) {
    return `要求 ≥ ${element.pmi_min}%`;
  }
  if (element.pmi_max !== null) {
    return `要求 ≤ ${element.pmi_max}%`;
  }
  return element.required ? '必检元素' : '非必检元素，不参与判定';
}

function getElementTone(element: MyPmiDetectionHistoryElement) {
  if (!element.required) {
    return 'none';
  }
  if (element.passed === false || element.value === null) {
    return 'fail';
  }
  if (element.passed === true) {
    return 'pass';
  }
  return 'none';
}

function getElementResultText(element: MyPmiDetectionHistoryElement) {
  if (!element.required) {
    return '仅记录';
  }
  if (element.passed === false || element.value === null) {
    return '不合格';
  }
  if (element.passed === true) {
    return '合格';
  }
  return '未判定';
}

function sortHistoryElements(elements: MyPmiDetectionHistoryElement[]) {
  return elements.slice().sort((left, right) => {
    if (left.required !== right.required) {
      return left.required ? -1 : 1;
    }
    return left.element.localeCompare(right.element, 'zh-CN', { sensitivity: 'base' });
  });
}

function getResultTone(passed: boolean | null) {
  if (passed === true) {
    return 'pass';
  }
  if (passed === false) {
    return 'fail';
  }
  return 'none';
}

function getResultText(passed: boolean | null) {
  if (passed === true) {
    return '合格';
  }
  if (passed === false) {
    return '不合格';
  }
  return '—';
}

function MyPmiDetectionHistory() {
  const requestIdRef = useRef(0);
  const detailRequestIdRef = useRef(0);
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all');
  const [items, setItems] = useState<MyPmiDetectionHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<MyPmiDetectionHistoryItem | null>(null);
  const [detail, setDetail] = useState<MyPmiDetectionHistoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const loadHistory = useCallback(async (
    nextPage: number,
    append: boolean,
    filter: ResultFilter,
  ) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setErrorMessage('');
      setItems([]);
      setTotal(0);
    }

    try {
      const response = await getMyPmiDetectionHistory({
        page: nextPage,
        page_size: PAGE_SIZE,
        ...(filter === 'all' ? {} : { result: filter }),
      });
      if (requestId !== requestIdRef.current) {
        return;
      }

      const data = response.data;
      const nextItems = Array.isArray(data.items) ? data.items : [];
      setItems((current) => append ? current.concat(nextItems) : nextItems);
      setPage(data.page || nextPage);
      setTotal(data.total || 0);
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      const message = getPdaErrorMessage(error, '检测历史加载失败');
      if (!append) {
        setErrorMessage(message);
      }
      notifyError(message);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadHistory(1, false, resultFilter);
  }, [loadHistory, resultFilter]);

  const loadDetail = useCallback(async (item: MyPmiDetectionHistoryItem) => {
    const requestId = detailRequestIdRef.current + 1;
    detailRequestIdRef.current = requestId;
    setSelectedRecord(item);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const response = await getMyPmiDetectionHistoryDetail(item.id);
      if (requestId !== detailRequestIdRef.current) {
        return;
      }
      setDetail(response.data);
    } catch (error) {
      if (requestId !== detailRequestIdRef.current) {
        return;
      }
      const message = getPdaErrorMessage(error, '检测详情加载失败');
      setDetailError(message);
      notifyError(message);
    } finally {
      if (requestId === detailRequestIdRef.current) {
        setDetailLoading(false);
      }
    }
  }, []);

  const closeDetail = useCallback(() => {
    detailRequestIdRef.current += 1;
    setSelectedRecord(null);
    setDetail(null);
    setDetailError('');
    setDetailLoading(false);
  }, []);

  const hasMore = items.length < total;

  function renderRecord(item: MyPmiDetectionHistoryItem) {
    const resultTone = getResultTone(item.passed);
    const displayCode = item.trace_code || item.inspection_serial || '--';

    return (
      <article className={`pmi-history-record pmi-history-record--${resultTone}`} key={item.id}>
        <div className="pmi-history-record__top">
          <strong>{displayCode}</strong>
          <span className={`pmi-history-result pmi-history-result--${resultTone}`}>
            {getResultText(item.passed)}
          </span>
        </div>
        <div className="pmi-history-record__grade">{item.material_grade || '--'}</div>
        <div className="pmi-history-record__meta">
          <span>{[item.customer_code, item.order_no].filter(Boolean).join(' · ') || '--'}</span>
          <span>{item.inspection_serial || '--'}</span>
        </div>
        <div className="pmi-history-record__foot">
          <time dateTime={item.detected_at}>{formatDetectedAt(item.detected_at)}</time>
        </div>
        {item.detail ? <p>{item.detail}</p> : null}
        <button
          aria-label={`查看检测详情 ${displayCode}`}
          className="pmi-history-record__detail"
          onClick={() => void loadDetail(item)}
          type="button"
        >
          查看详情
        </button>
      </article>
    );
  }

  if (selectedRecord) {
    const resultRecord = detail || selectedRecord;
    const resultTone = getResultTone(resultRecord.passed);
    const displayCode = resultRecord.trace_code || resultRecord.inspection_serial || '--';
    const elements = sortHistoryElements(detail?.elements || []);
    const detailFields = detail ? [
      ['光谱记录', detail.replicate_id],
      ['检测序号', detail.replicate_no],
      ['光谱文件', detail.file_name],
      ['样品名称', detail.sample_name],
      ['检测仪器', detail.instrument],
      ['测量时间', formatDetailDate(detail.measured_at)],
      ['检测牌号', detail.grade || detail.material_grade],
      ['仪器原始状态', detail.raw_status],
    ] : [];

    return (
      <PdaPage
        actionIcon={<RefreshCw aria-hidden="true" size={16} strokeWidth={2.3} />}
        actionLabel={detailLoading ? '刷新中' : '刷新'}
        onAction={() => void loadDetail(selectedRecord)}
        onBack={closeDetail}
        pageClassName="pmi-detection-history-page"
        subtitle={resultRecord.inspection_serial || undefined}
        title="检测详情"
      >
        <section className={`pmi-history-detail-summary pmi-history-detail-summary--${resultTone}`}>
          <div>
            <span>检测当时结论</span>
            <strong>{displayCode}</strong>
          </div>
          <span className={`pmi-history-result pmi-history-result--${resultTone}`}>
            {getResultText(resultRecord.passed)}
          </span>
        </section>

        {detailLoading ? (
          <section aria-label="检测详情加载中" className="pmi-history-skeleton-list" role="status">
            <div className="pmi-history-skeleton-card"><span /><span /><span /></div>
          </section>
        ) : null}

        {!detailLoading && detailError ? (
          <section className="pmi-history-state pmi-history-state--error">
            <TriangleAlert aria-hidden="true" size={30} strokeWidth={2} />
            <h2>检测详情加载失败</h2>
            <p>{detailError}</p>
            <button onClick={() => void loadDetail(selectedRecord)} type="button">重新加载</button>
          </section>
        ) : null}

        {!detailLoading && !detailError && detail ? (
          <>
            <dl className="pmi-history-detail-fields">
              {detailFields.map(([label, value]) => (
                <div key={String(label)}>
                  <dt>{label}</dt>
                  <dd>{value === null || value === '' ? '--' : String(value)}</dd>
                </div>
              ))}
            </dl>

            {detail.fail_reason ? (
              <section className="pmi-history-detail-reason">
                <strong>不合格原因</strong>
                <p>{detail.fail_reason}</p>
              </section>
            ) : null}

            <section className="pmi-history-elements-section">
              <div className="pmi-history-elements-head">
                <strong>元素详情</strong>
                <span>{elements.length} 项</span>
              </div>
              {elements.length ? (
                <div className="pmi-history-element-list">
                  {elements.map((element, index) => {
                    const tone = getElementTone(element);
                    return (
                      <article
                        className={`pmi-history-element pmi-history-element--${tone}`}
                        key={`${element.element}-${index}`}
                      >
                        <div className="pmi-history-element__top">
                          <strong>{element.element}</strong>
                          {element.required ? <span className="pmi-history-element__required">必检</span> : null}
                          <span className={`pmi-history-element__result pmi-history-element__result--${tone}`}>
                            {getElementResultText(element)}
                          </span>
                        </div>
                        <div className="pmi-history-element__value">{formatElementValue(element.value)}</div>
                        <div className="pmi-history-element__range">{formatElementRange(element)}</div>
                        {element.reason ? <p>{element.reason}</p> : null}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="pmi-history-elements-empty">
                  <strong>暂无元素明细</strong>
                  <p>该历史记录只能显示基本信息，不代表接口加载失败。</p>
                </div>
              )}
            </section>
          </>
        ) : null}
      </PdaPage>
    );
  }

  return (
    <PdaPage
      actionIcon={<RefreshCw aria-hidden="true" size={16} strokeWidth={2.3} />}
      actionLabel={loading ? '刷新中' : '刷新'}
      onAction={() => void loadHistory(1, false, resultFilter)}
      onBack={() => navigateTo('/pmi-continuous')}
      pageClassName="pmi-detection-history-page"
      title="我的检测历史"
    >
      <section aria-label="历史筛选" className="pmi-history-filters">
        {(['all', 'pass', 'fail'] as ResultFilter[]).map((filter) => (
          <button
            aria-pressed={resultFilter === filter}
            className={resultFilter === filter ? 'pmi-history-filter pmi-history-filter--active' : 'pmi-history-filter'}
            key={filter}
            onClick={() => setResultFilter(filter)}
            type="button"
          >
            {{ all: '全部', fail: '不合格', pass: '合格' }[filter]}
          </button>
        ))}
      </section>

      <div className="pmi-history-list-head">
        <strong>检测记录</strong>
        {!loading && !errorMessage ? <span>共 {total} 条</span> : null}
      </div>

      {loading ? (
        <section aria-label="检测历史加载中" className="pmi-history-skeleton-list" role="status">
          {[1, 2, 3].map((key) => (
            <div className="pmi-history-skeleton-card" key={key}>
              <span />
              <span />
              <span />
            </div>
          ))}
        </section>
      ) : null}

      {!loading && errorMessage ? (
        <section className="pmi-history-state pmi-history-state--error">
          <TriangleAlert aria-hidden="true" size={30} strokeWidth={2} />
          <h2>检测历史加载失败</h2>
          <p>{errorMessage}</p>
          <button onClick={() => void loadHistory(1, false, resultFilter)} type="button">重新加载</button>
        </section>
      ) : null}

      {!loading && !errorMessage && !items.length ? (
        <section className="pmi-history-state">
          <Inbox aria-hidden="true" size={30} strokeWidth={2} />
          <h2>暂无检测记录</h2>
          <p>当前结果筛选下没有检测历史</p>
        </section>
      ) : null}

      {!loading && !errorMessage && items.length ? (
        <section className="pmi-history-record-list">
          {items.map(renderRecord)}
        </section>
      ) : null}

      {!loading && !errorMessage && hasMore ? (
        <button
          className="pmi-history-load-more"
          disabled={loadingMore}
          onClick={() => void loadHistory(page + 1, true, resultFilter)}
          type="button"
        >
          {loadingMore ? '加载中…' : '加载更多记录'}
        </button>
      ) : null}
    </PdaPage>
  );
}

export default MyPmiDetectionHistory;
